import {
  useCreateStatusFlagMutation,
  useUpdateFlagIconMutation,
  useUpdateStatusFlagMutation,
} from "app/services/api/statusFlagsApi";
import { StatusFlagModel } from "app/services/api/types/statusFlags/models/StatusFlagModel";
import { CreateFlagRequest } from "app/services/api/types/statusFlags/requests/CreateFlagRequest";
import { useForm } from "react-hook-form";
import { useDisplayToast } from "ui/hooks/useDisplayToast";
import * as mime from "react-native-mime-types";
import { useEffect } from "react";

const createFormDataFromIcon = async (icon: CreateFlagRequest["icon"]) => {
  if (!icon?.uri || !icon?.name || !icon?.mimeType) return null;

  const response = await fetch(icon.uri);
  const blob = await response.blob();
  return new File([blob], icon.name, { type: icon.mimeType });
};

export const useHandleCreateEditFlag = (
  onSuccess?: () => void,
  flag?: StatusFlagModel | null,
) => {
  const { showNewToast, showErrorToast } = useDisplayToast();
  const [triggerCreateStatusFlag, { isLoading: isCreateLoading }] =
    useCreateStatusFlagMutation();
  const [triggerUpdateStatusFlag, { isLoading: isUpdateLoading }] =
    useUpdateStatusFlagMutation();
  const [triggerUpdateFlagIcon, { isLoading: isIconLoading }] =
    useUpdateFlagIconMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { dirtyFields },
  } = useForm<CreateFlagRequest>();

  const uploadFlagIcon = async (
    icon: CreateFlagRequest["icon"] | undefined,
    flagId: number,
  ) => {
    try {
      const formData = new FormData();

      if (icon) {
        const file = await createFormDataFromIcon(icon);
        if (!file) throw new Error("Failed to create file from icon");
        formData.append("icon", file);
      } else {
        formData.append("icon", "");
      }

      await triggerUpdateFlagIcon({ flag: flagId, body: formData }).unwrap();
    } catch (error) {
      showErrorToast(error);
    }
  };

  useEffect(() => {
    if (flag) {
      reset({
        name: flag.name,
        visible: flag.visible,
        icon: flag.icon
          ? {
              name: flag.icon.file_name,
              uri: flag.icon.url,
              mimeType: mime.lookup(flag.icon.file_name) || "image/jpeg",
            }
          : undefined,
      });
    }
  }, [flag, reset]);

  const createFormData = async (formData: CreateFlagRequest) => {
    const data = new FormData();
    data.append("name", formData.name);
    data.append("visible", formData.visible ? "1" : "0");

    const file = await createFormDataFromIcon(formData.icon);
    if (file) data.append("icon", file);

    return data;
  };

  const handleCreateFlag = async (formData: CreateFlagRequest) => {
    try {
      const data = await createFormData(formData);
      await triggerCreateStatusFlag(data).unwrap();

      showNewToast({
        title: "Status flag created",
        message: "The status flag was successfully created.",
        type: "success",
      });
      onSuccess?.();
    } catch (e) {
      showErrorToast(e);
      console.log(e);
    }
  };

  const handleUpdateFlag = async (formData: CreateFlagRequest) => {
    if (!flag?.id) return;

    try {
      const { icon, ...updateBody } = formData;
      await triggerUpdateStatusFlag({
        flag: flag.id,
        body: updateBody,
      }).unwrap();
      if (dirtyFields.icon) {
        await uploadFlagIcon(icon, flag.id);
      }
      showNewToast({
        title: "Status flag updated",
        message: "The status flag was successfully updated.",
        type: "success",
      });
      onSuccess?.();
    } catch (e) {
      showErrorToast(e);
      console.log(e);
    }
  };

  const handleFlagSubmission = handleSubmit(async (formData) => {
    if (flag?.id) {
      await handleUpdateFlag(formData);
    } else {
      await handleCreateFlag(formData);
    }
  });

  return {
    control,
    isLoadingAction: isCreateLoading || isUpdateLoading || isIconLoading,
    handleFlagSubmission,
  };
};
